import React, { useEffect, useState } from "react";
import useStyles from "../../../components/common/model/styles";
import Popup from "../../../components/common/model/Popup";
import {
  Button,
  Checkbox,
  DialogContent,
  DialogContentText,
  Grid,
  Typography,
} from "@mui/material";
import UserInfoForm from "./UserInfoForm";
import Notification from "../../../components/common/notification/Notification";
import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";
import Validation from "../../../utils/Validation";
import ResetFormAlert from "./ResetFormAlert";
import {
  addSubUserAction,
  setAddSubUserResponse,
  setNewSubUserLocal,
  updateSubUserAction,
  updateSubUserResponse,
} from "../../../store/reducers/subUserSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  setNotificationAction,
  setToastAction,
} from "../../../store/reducers/mmymSlice";
import { setCheckUniqueEmailAction } from "../../../store/reducers/adminClientsSlice";

interface UserDetail {
  name: string | null;
  email: string | null;
  roles?: string | string[]; // can be string or array
  phone: string | null;
  aka_name: string | null;
}

interface ManageUserProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectUser: any;
}

export default function ManageUser(props: ManageUserProps) {
  const { selectUser, onHide, open, title } = props;

  const initialUserDetail: UserDetail = {
    name: "",
    email: "",
    roles: [], // ✅ empty array instead of empty string
    phone: "",
    aka_name: "",
  };

  const [userDetail, setUserDetail] = useState<UserDetail>(initialUserDetail);
  const dispatch = useDispatch();
  const { isEmailError } = useSelector((state: any) => state?.adminClients);
  const {
    newUserLocal,
    userRoles,
    isAddSubUserSuccess,
    isUpdateSubUserSuccess,
  } = useSelector((state: any) => state.subUser);

  const [fullWidth, setFullWidth] = useState(true);
  const [maxWidth, setMaxWidth] = useState<
    "sm" | "xs" | "md" | "lg" | "xl" | false
  >("lg");
  const [isActionCalled, setIsActionCalled] = useState(false);
  const [countryCode, setCountryCode] = useState<string>("+44");
  const [resetForm, setResetForm] = useState(false);
  const [isEmailActionCalled, setIsEmailActionCalled] = useState(false);
  const [roleModules, setRoleModules] = useState<any[]>([]);
  const [alertModelShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<string | null>(null);

  const classes = useStyles();

  const hideHandler = () => {
    onHide();
    handleClear();
    dispatch(setNotificationAction({ message: [], severity: "error" }));
    dispatch(setAddSubUserResponse(false));
    dispatch(updateSubUserResponse(false));
    setUserDetail(initialUserDetail);
    dispatch(setCheckUniqueEmailAction(false));
    setRoleModules([]);
    setOriginalEmail(null);
    document.body.style.cursor = "default";
  };

  // --- Sanitize roles for validation ---
  const sanitizedRoles =
    Array.isArray(userDetail.roles) &&
    userDetail.roles.length === 1 &&
    userDetail.roles[0] === ""
      ? []
      : userDetail.roles;

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    name: userDetail?.name,
    email: userDetail?.email,
    roles: sanitizedRoles, // ✅ pass sanitized roles
    phone: userDetail?.phone,
  });

  useEffect(() => {
    if (selectUser) {
      const extractedCountryCode = selectUser.phone?.match(/^\+91|\+44/);
      setCountryCode(extractedCountryCode ? extractedCountryCode[0] : "+44");
      setOriginalEmail(selectUser.email);

      const rolesArray = Array.isArray(selectUser.roles)
        ? selectUser.roles
        : selectUser.roles?.split(",").filter((r: string) => r.trim() !== "") ||
          [];

      const formattedUser = {
        ...initialUserDetail,
        ...selectUser,
        roles: rolesArray,
        phone: selectUser.phone
          ? selectUser.phone.replace(/^\+91|\+44/, "")
          : "",
      };

      setUserDetail(formattedUser);
      dispatch(setNewSubUserLocal(formattedUser));
    }
  }, [selectUser]);

  const handleInputChange = (field: string, value: any) => {
    const sanitizedValue =
      field === "roles" && Array.isArray(value)
        ? value.filter((role) => role && role.trim() !== "") // ✅ remove empty strings
        : value;

    setUserDetail((prev) => ({ ...prev, [field]: sanitizedValue }));

    dispatch(setNewSubUserLocal({ ...newUserLocal, [field]: sanitizedValue }));

    resetValidator(field);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      const formattedUserDetail = {
        ...userDetail,
        phone: userDetail?.phone ? `${countryCode}${userDetail?.phone}` : "",
      };

      if (!isError() && error !== null && isActionCalled && !isEmailError) {
        if (selectUser?.staff_id) {
          dispatch(updateSubUserAction(formattedUserDetail));
        } else {
          dispatch(addSubUserAction(formattedUserDetail));
        }
      }

      setIsActionCalled(false);
    }
  }, [isActionCalled]);

  const openClearPopup = () => setAlertModelShow(true);

  useEffect(() => {
    if (isClickReset) {
      handleClear();
      setAlertModelShow(false);
    }
  }, [isClickReset]);

  const handleClear = () => {
    if (selectUser?.staff_id) {
      setUserDetail(selectUser);
      dispatch(setNewSubUserLocal(selectUser));
    } else {
      setUserDetail(initialUserDetail);
      dispatch(setNewSubUserLocal(initialUserDetail));
      setRoleModules([]);
    }
    setResetForm(true);
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
    setIsEmailActionCalled(false);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  useEffect(() => {
    if (isAddSubUserSuccess || isUpdateSubUserSuccess) {
      if (
        isUpdateSubUserSuccess &&
        originalEmail &&
        userDetail.email &&
        userDetail.email !== originalEmail
      ) {
        dispatch(
          setToastAction({
            message: "Email has been changed. Please resend the password.",
            appearance: "warning",
          }),
        );
      }
      hideHandler();
    }
  }, [isAddSubUserSuccess, isUpdateSubUserSuccess]);

  useEffect(() => {
    const rolesArray: string[] = Array.isArray(userDetail?.roles)
      ? userDetail.roles.filter((r) => r.trim() !== "")
      : typeof userDetail?.roles === "string" && userDetail?.roles
        ? userDetail?.roles?.split(",").filter((r: any) => r.trim() !== "")
        : [];

    if (rolesArray.length > 0) {
      const matchedRoles = userRoles.filter((role: any) =>
        rolesArray.includes(role?.role),
      );
      if (matchedRoles.length > 0) {
        const moduleMap = new Map<string, any>();
        matchedRoles.forEach((role: any) => {
          role.modules.forEach((mod: any) => {
            const existing = moduleMap.get(mod.module_name);
            if (
              !existing ||
              (mod.permissions?.length ?? 0) >
                (existing.permissions?.length ?? 0)
            ) {
              moduleMap.set(mod.module_name, mod);
            }
          });
        });
        const uniqueModules = Array.from(moduleMap.values());
        setRoleModules([{ role: "Combined", modules: uniqueModules }]);
      } else {
        setRoleModules([]);
      }
    } else {
      setRoleModules([]);
    }
  }, [userDetail.roles, userRoles]);

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#fff"
        header={
          <>
            <img
              onClick={(e: any) => handleSubmit(e)}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />
            <img
              onClick={openClearPopup}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="delete"
            />
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "10px" }}>
          <DialogContentText>
            <>
              <Notification />
              <UserInfoForm
                selectUser={selectUser}
                userDetail={userDetail}
                onChange={handleInputChange}
                error={error}
                resetValidator={resetValidator}
                checkValidator={checkValidator}
                isEmailActionCalled={isEmailActionCalled}
                setIsEmailActionCalled={setIsEmailActionCalled}
                setCountryCode={setCountryCode}
                countryCode={countryCode}
              />
              {/* Permissions grid rendering remains same */}
              <Grid container spacing={2} style={{ marginTop: "6px" }}>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  style={{
                    border: "1px solid #CFCFCF",
                    borderTopLeftRadius: "5px",
                    padding: "8px",
                    background: "#E5F4F1",
                  }}
                >
                  <Typography
                    style={{
                      color: "#333333",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Permissions
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={8}
                  style={{
                    background: "#E5F4F1",
                    padding: "8px",
                    border: "1px solid #CFCFCF",
                    borderTopRightRadius: "5px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  <Grid container>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Create
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Read
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Update
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Delete
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{
                    background: "#fff",
                    paddingBottom: "6px",
                    border: "1px solid #CFCFCF",
                  }}
                >
                  {roleModules.map((module) => (
                    <>
                      {" "}
                      {module?.modules.map((item: any) => (
                        <Grid
                          container
                          key={module.module_name}
                          spacing={2}
                          style={{ marginTop: "2px" }}
                        >
                          <Grid
                            item
                            xs={12}
                            sm={4}
                            style={{ padding: "4px 8px" }}
                          >
                            <Typography
                              style={{ color: "#333333", fontWeight: 600 }}
                            >
                              {item?.module_name.replace(/:/g, ": ")}
                            </Typography>
                          </Grid>

                          <>
                            {["create", "read", "update", "delete"].map(
                              (action) => (
                                <Grid
                                  item
                                  xs={2}
                                  key={action}
                                  style={{ padding: "4px 8px" }}
                                >
                                  <Checkbox
                                    disabled
                                    style={{ padding: "0px" }}
                                    size="small"
                                    checked={item?.permissions.includes(action)}
                                    name={`${item.module_name}-${action}`}
                                  />
                                </Grid>
                              ),
                            )}
                          </>
                        </Grid>
                      ))}
                    </>
                  ))}
                </Grid>
              </Grid>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alertModelShow && (
        <ResetFormAlert
          open={alertModelShow}
          title=""
          alertModalShow={alertModelShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
}
